import React from 'react';
import PropTypes from 'prop-types';
import Button from 'pencil/Button';
import * as I18n from 'circe/I18n';
import { useWidth } from 'domain/fuse/layout/utils';
import AccountConvertIcon from 'mdi-react/AccountConvertIcon';
import cx from 'classnames';
import MenuItem from 'pencil/MenuItem';
import Menu from 'pencil/Menu';
import { getRoleName } from 'domain/fuse/auth/utils';
import Typography from 'pencil/Typography';
import Person from 'pencil/icons/Person';
import makeStyles from 'pencil/styles/makeStyles';
import useTheme from 'pencil/styles/useTheme';
import CircleIcon from 'mdi-react/CircleIcon';
import Divider from 'pencil/Divider';
import BriefcaseAccountIcon from 'mdi-react/BriefcaseAccountIcon';
import PowerCycleIcon from 'mdi-react/PowerCycleIcon';
import CogIcon from 'mdi-react/CogIcon';
import { withRouter } from 'react-router-dom';

import BuildingIcon from 'mdi-react/BuildingIcon';
import grey from 'pencil/colors/grey';
import { PROFILE_STATUS } from '../constants';
import { NOTIFICATIONS } from 'domain/layout/constants';
const useStyles = makeStyles(
  (theme) => ({
    notificationItem: {
      // height: 10,
    },
    pendingProfile: {
      color: grey[500],
    },
    activeProfile: {
      color: '#000 !important',
      opacity: '1 !important',
    },
    smallIcon: {
      marginTop: 7,
      marginLeft: 20,
      position: 'absolute',
    },
    profile: {
      display: 'block',
    },
    profileBtn: {
      textTransform: 'none',
      textAlign: 'left',
      lineHeight: 0.8,
      padding: theme.spacing(0, 2),
    },
    currentUserRole: {
      textAlign: 'left',
      paddingRight: theme.spacing(1),
      textTransform: 'none',
      lineHeight: 0.8,

      '& > *': {
        lineHeight: 1,
      },
    },
    userAccountDetails: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.secondary.contrastText,
      '& > *': {
        lineHeight: 1.2,
      },
    },
    accountSeparator: {
      fill: theme.palette.secondary.contrastText,
      margin: '0 3px',
    },
    profileSelector: {
      textTransform: 'none',
    },
    accountIcon: {
      marginRight: theme.spacing(1),
    },
    profileListItem: {
      minHeight: theme.spacing(4),
      minWidth: 230,
      paddingLeft: theme.spacing(3),
    },
    domainTitle: {
      fontWeight: 300,
      textTransform: 'uppercase',
      minHeight: theme.spacing(2),
      opacity: '1 !important',
      color: '#000 !important',
    },
    domainTitleColor: {
      color: '#000 !important',
    },
    justifyMenuItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    paperStyles: {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        '-webkit-appearance': 'none',
      },
      '&::-webkit-scrollbar:vertical': {
        width: 11,
      },
      '&::-webkit-scrollbar:horizontal': {
        height: 11,
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: 8,
        border: '2px solid white',
        backgroundColor: 'rgba(0, 0, 0, .5)',
      },
    },
  }),
  { name: 'Profile-Selector' },
);

const ProfileSelector = (props) => {
  const {
    profiles = [],
    currentProfile,
    select,
    isAccountAdmin = false,
    user: userData,
    onCompanyCodeClick,
    onLogout,
    history,
  } = props;
  const classes = useStyles(props);
  const width = useWidth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const shouldShow = profiles.length > -1;
  const openMenu = React.useMemo(() => Boolean(anchorEl), [anchorEl]);
  const name = userData && userData.given_name ? userData.given_name : userData.name;
  const theme = useTheme();

  const profilesByDomain = React.useMemo(
    () =>
      profiles.reduce((acc, profile) => {
        const domain = profile.domain != 'string' ? profile.domain : 'UNLINKED';
        if (acc[domain] === undefined) {
          acc[domain] = [];
        }
        acc[domain].push(profile);
        return acc;
      }, {}),
    [profiles],
  );

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <Button
        aria-owns={openMenu ? 'profile-menu' : undefined}
        aria-haspopup="true"
        onClick={handleMenu}
        className={classes.profileBtn}
      >
        <Person className={classes.accountIcon} />
        <div className={classes.currentUserRole}>
          <Typography variant="body2" color="textPrimary" component="div">
            {width !== 'xs' && I18n.str('hiName', 'Hi, {name}', { name })}
          </Typography>
          <div className={classes.userAccountDetails}>
            <Typography variant="caption" color="textSecondary">
              {getRoleName(currentProfile.role)}
            </Typography>
            <CircleIcon size={4} className={classes.accountSeparator} />
            <Typography component="span" color="textSecondary" variant="caption">
              {currentProfile?.accountInfo?.name}
            </Typography>
          </div>
        </div>
      </Button>
      <Menu
        className={classes.menu}
        id="profile-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={openMenu}
        onClose={handleClose}
        classes={{
          paper: classes.paperStyles,
        }}
      >
        {isAccountAdmin && (
          <div>
            <MenuItem
              onClick={() => {
                onCompanyCodeClick();
                handleClose();
              }}
              className={cx(classes.justifyMenuItem)}
            >
              <Typography variant="body2">{I18n.str('companyCode', 'Company Code')}</Typography>
              <BuildingIcon size={18} />
            </MenuItem>
            <Divider />
            <MenuItem
              className={classes.justifyMenuItem}
              onClick={() => {
                history.push('/settings');
              }}
            >
              <Typography variant="body2">{I18n.str('settings', 'Settings')}</Typography>
              <CogIcon size={18} />
            </MenuItem>
          </div>
        )}
        {isAccountAdmin && <Divider />}

        {shouldShow && (
          <div>
            {Object.keys(profilesByDomain).map((domain) => (
              <div className={classes.domainProfilesWrapper} key={domain}>
                <MenuItem disabled className={cx(classes.domainTitle, classes.justifyMenuItem)}>
                  <Typography variant="caption" className={classes.domainTitleColor}>
                    {I18n.str('domain', '{domain} profiles', { domain })}
                  </Typography>
                  <BriefcaseAccountIcon size={16} />
                </MenuItem>

                {profilesByDomain[domain].map((profile) => {
                  const { status, role, profileId, domain } = profile;

                  return (
                    <MenuItem
                      key={profileId}
                      classes={{
                        root: cx(classes.profileListItem, classes.notificationItem, {
                          [classes.activeProfile]:
                            PROFILE_STATUS.ACTIVE || profileId === currentProfile.profileId,
                        }),
                      }}
                      onClick={() => {
                        select(profile);
                        handleClose();
                      }}
                      data-qa={`profile-selector-${profileId}`}
                      disabled={
                        status !== PROFILE_STATUS.ACTIVE || profileId === currentProfile.profileId
                      }
                    >
                      <div className={classes.profile}>
                        <Typography
                          variant="body2"
                          className={cx({
                            [classes.pendingProfile]: status !== PROFILE_STATUS.ACTIVE,
                            [classes.activeProfile]: profileId === currentProfile.profileId,
                          })}
                          component="div"
                          color="secondary"
                        >
                          {getRoleName(role)}
                        </Typography>
                      </div>
                    </MenuItem>
                  );
                })}

                <Divider />
              </div>
            ))}
          </div>
        )}

        <Divider />

        <MenuItem
          className={classes.justifyMenuItem}
          onClick={() => {
            onLogout();
            localStorage.removeItem(NOTIFICATIONS.key);
          }}
        >
          <Typography variant="body2">{I18n.str('logout', 'Logout')}</Typography>
          <PowerCycleIcon size={18} />
        </MenuItem>
      </Menu>
    </div>
  );
};

ProfileSelector.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      profileId: PropTypes.string,
      domain: PropTypes.string,
    }),
  ),
  currentProfile: PropTypes.shape({
    profileId: PropTypes.string,
    domain: PropTypes.string,
  }).isRequired,
  select: PropTypes.func.isRequired,
  isAccountAdmin: PropTypes.bool,
  user: PropTypes.shape({
    given_name: PropTypes.string,
  }).isRequired,
  onCompanyCodeClick: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

ProfileSelector.defaultProps = {
  profiles: [],
  isAccountAdmin: false,
};

export default withRouter(ProfileSelector);
